import { Box } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { GoalDetailOwnFunds } from "web_myplann/Goals/Components/GoalDetailOwnFunds";
import {
	GoalDetailSolution,
	type PinStep,
} from "web_myplann/Goals/Components/GoalDetailSolution";
import { withLockGuard } from "web_myplann/Goals/Helpers/withLockGuard";
import { useGoalPortfolioActions } from "web_myplann/Goals/Hooks/useGoalPortfolioActions";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	type GoalDetailResponseDTO,
	type GoalPortfolioListItemResponseDTO,
	GoalPortfolioTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";

export type WelcomePinTrigger = { hasOwnFunds: boolean };

type Props = {
	goal: GoalDetail;
	goalDto: GoalDetailResponseDTO | undefined;
	welcomePinTrigger: WelcomePinTrigger | null;
	onWelcomePinTriggerHandled: () => void;
	onLockedAction: (() => void) | null;
};

export const GoalPinningSection: FC<Props> = ({
	goal,
	goalDto,
	welcomePinTrigger,
	onWelcomePinTriggerHandled,
	onLockedAction,
}) => {
	const [pinStep, setPinStep] = useState<PinStep>("closed");
	const [pinPortfolioType, setPinPortfolioType] =
		useState<GoalPortfolioTypeEnum>(GoalPortfolioTypeEnum.Allocated);

	const closePin = () => setPinStep("closed");

	const { pin, unpin, editAllocation, unpinningId } = useGoalPortfolioActions({
		goalId: Number(goal.id),
		goalDto,
		onPinSettled: closePin,
	});

	useEffect(() => {
		if (!welcomePinTrigger) return;
		if (onLockedAction) {
			onWelcomePinTriggerHandled();
			return;
		}
		const { hasOwnFunds } = welcomePinTrigger;
		setPinPortfolioType(
			hasOwnFunds
				? GoalPortfolioTypeEnum.SetAside
				: GoalPortfolioTypeEnum.Allocated,
		);
		setPinStep(hasOwnFunds ? "choose" : "existing");
		onWelcomePinTriggerHandled();
	}, [welcomePinTrigger, onWelcomePinTriggerHandled, onLockedAction]);

	const openPinStep = withLockGuard(onLockedAction, (next: PinStep) => {
		if (next === "choose") {
			setPinPortfolioType(GoalPortfolioTypeEnum.Allocated);
		}
		setPinStep(next);
	});

	const handleSolutionPinStep = (next: PinStep) => {
		if (next === "closed") {
			closePin();
			return;
		}
		openPinStep(next);
	};

	const handleOwnFundsPin = withLockGuard(onLockedAction, () => {
		setPinPortfolioType(GoalPortfolioTypeEnum.SetAside);
		setPinStep("choose");
	});

	const handleUnpin = withLockGuard(onLockedAction, unpin);

	const handlePinSelect = (picked: GoalPortfolioListItemResponseDTO) => {
		pin(picked, pinPortfolioType);
	};

	const { allocated, setAside } = useMemo(
		() => ({
			allocated: goal.portfolios.filter(
				(p) => p.type === GoalPortfolioTypeEnum.Allocated,
			),
			setAside: goal.portfolios.filter(
				(p) => p.type === GoalPortfolioTypeEnum.SetAside,
			),
		}),
		[goal.portfolios],
	);

	const hasOwnFunds = goal.ownFunds > 0;

	return (
		<>
			{hasOwnFunds && (
				<Box mt={1}>
					<GoalDetailOwnFunds
						goal={goal}
						portfolios={setAside}
						onAddPin={handleOwnFundsPin}
						onUnpin={handleUnpin}
						unpinningId={unpinningId}
					/>
				</Box>
			)}
			<Box mt={1}>
				<GoalDetailSolution
					goal={goal}
					allocatedPortfolios={allocated}
					pinStep={pinStep}
					onPinStepChange={handleSolutionPinStep}
					onPinSelect={handlePinSelect}
					onUnpin={handleUnpin}
					onEditAllocation={editAllocation}
					unpinningId={unpinningId}
					onLockedAction={onLockedAction}
				/>
			</Box>
		</>
	);
};
